// Overtime — a web recreation of the desktop "OvertimePrint / Name Manager". Manage an employee roster
// (add by hand, import many from Excel, or load the standard list), choose the month, tick who to
// include, and print one monthly overtime card per employee: a blank sheet (Greek headers, one row per
// day of the chosen month with its weekday) filled in by hand. Cards render in the browser and print
// with the Print button (the desktop tool wrote an .xlsx per employee and printed it through Excel).

import { useMemo, useRef, useState } from 'react';
import {
  useAddOvertimeEmployee,
  useImportOvertime,
  useOvertime,
  useRemoveOvertimeEmployee,
  useSortOvertime,
} from '../features/hooks.js';
import { OVERTIME_DEFAULT_NAMES } from '@technohull/shared';
import { parseNamesWorkbook } from '../features/overtimeImport.js';
import { useAuth } from '../auth/AuthContext.js';
import { fold, hasText } from '../lib/search.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, PageHeader, useConfirm } from '../components/ui.js';
import { IconTrash } from '../components/icons.js';

// Printing an overtime card: the page itself has no margin (so no browser header or footer fits) and the
// card carries the 13mm instead. Rendered by this page only, so other printouts keep their margins.
const OT_PRINT_PAGE = `
@page { size: A4 portrait; margin: 0; }
@media print {
  html, body { background: #fff !important; }
  .ot-print-area .ot-card { padding: 13mm !important; box-sizing: border-box; }
}
`;

// the current roster, offered as a one-click seed ("Load standard list"). Single source of truth in
// @technohull/shared so the frontend list and the D1 deploy seed can't drift apart.
const DEFAULT_NAMES = OVERTIME_DEFAULT_NAMES;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const daysInMonth = (y: number, m0: number) => new Date(y, m0 + 1, 0).getDate();
const weekday = (y: number, m0: number, d: number) => new Date(y, m0, d).toLocaleDateString('en-US', { weekday: 'long' });

export function Overtime() {
  const { can } = useAuth();
  const canWrite = can('production:write');
  const q = useOvertime();
  const add = useAddOvertimeEmployee();
  const remove = useRemoveOvertimeEmployee();
  const sort = useSortOvertime();
  const importList = useImportOvertime();
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmNode, confirm] = useConfirm();

  const employees = q.data?.employees ?? [];
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [newName, setNewName] = useState('');
  const [filter, setFilter] = useState('');
  const [excluded, setExcluded] = useState<Set<string>>(new Set()); // unchecked = not printed
  const [imp, setImp] = useState<{ names: string[]; fresh: string[]; file: string } | null>(null);
  const [impErr, setImpErr] = useState('');
  // phones preview the first card only (scaled down), behind a "Show all" toggle; print always gets them all
  const [allCards, setAllCards] = useState(false);

  const busy = add.isPending || remove.isPending || sort.isPending || importList.isPending;
  const years = useMemo(() => { const y = new Date().getFullYear(); return [y - 2, y - 1, y, y + 1]; }, []);
  const toPrint = employees.filter((n) => !excluded.has(n));
  const nameQuery = fold(filter.trim());
  const shown = nameQuery ? employees.filter((n) => hasText(nameQuery, n)) : employees;

  const submitAdd = () => { const n = newName.trim(); if (n) { add.mutate({ name: n }); setNewName(''); } };
  const removeName = async (name: string) => {
    if (await confirm({ title: 'Remove from roster', message: `Remove “${name}” from the overtime roster?`, confirmLabel: 'Remove', danger: true })) remove.mutate({ name });
  };
  const toggle = (name: string) => setExcluded((s) => { const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n; });

  const onFile = async (file: File) => {
    setImpErr('');
    try {
      const XLSX: unknown = await import('xlsx');
      const names = parseNamesWorkbook(XLSX, await file.arrayBuffer());
      if (!names.length) { setImpErr('No names found in that file.'); return; }
      const have = new Set(employees.map((e) => e.toUpperCase()));
      setImp({ names, fresh: names.filter((n) => !have.has(n.toUpperCase())), file: file.name });
    } catch {
      setImpErr('Could not read that file. Use an .xlsx, .xls or .csv with the names in a column.');
    }
  };

  return (
    <>
      <PageHeader title="Overtime">
        <span className="muted">Monthly overtime cards</span>
      </PageHeader>

      <div className="no-print">

        {/* toolbar: month/year + print */}
        <div className="ot-toolbar">
          <label className="ot-field">Month
            <select className="field" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </label>
          <label className="ot-field">Year
            <select className="field" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <span className="ot-toolbar-sp" />
          <span className="ot-count">{toPrint.length} of {employees.length} selected</span>
          <button className="btn btn-primary" disabled={!toPrint.length} onClick={() => window.print()}>
            Print {toPrint.length ? `(${toPrint.length})` : ''}
          </button>
        </div>

        {q.isLoading && <LoadingState label="Loading roster…" />}
        {q.error && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

        {q.data && (
          <div className="ot-manager">
            <div className="ot-manager-hd">
              <b>Employees</b>
              <span className="ot-badge">{employees.length}</span>
              <span className="ot-toolbar-sp" />
              {canWrite && (
                <>
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.currentTarget.value = ''; }} />
                  <button className="btn btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>Import from Excel</button>
                  <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => importList.mutate({ names: DEFAULT_NAMES })}>Load standard list</button>
                  <button className="btn btn-sm btn-ghost" disabled={busy || !employees.length} onClick={() => sort.mutate({})}>Sort A→Z</button>
                </>
              )}
            </div>

            {impErr && <InlineError error={new Error(impErr)} onDismiss={() => setImpErr('')} />}
            {(add.error ?? remove.error ?? sort.error ?? importList.error) && <InlineError error={add.error ?? remove.error ?? sort.error ?? importList.error} onDismiss={() => { add.reset(); remove.reset(); sort.reset(); importList.reset(); }} />}

            {canWrite && (
              <div className="ot-add">
                <input className="field" placeholder="Add one employee…" value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }} />
                <button className="btn btn-sm btn-primary" disabled={busy || !newName.trim()} onClick={submitAdd}>Add</button>
              </div>
            )}

            {employees.length > 0 && (
              <div className="ot-toolrow">
                <input className="field ot-search" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
                <span className="ot-toolbar-sp" />
                <button className="btn btn-sm btn-ghost" onClick={() => setExcluded(new Set())}>Select all</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setExcluded(new Set(employees))}>Select none</button>
              </div>
            )}

            {employees.length === 0 ? (
              <EmptyState title="No employees yet"
                hint={canWrite ? 'Add a name, import from Excel, or load the standard list.' : 'Ask an editor to add employees.'} />
            ) : (
              <ul className="ot-list">
                {shown.map((name) => {
                  const idx = employees.indexOf(name) + 1;
                  return (
                    <li key={name} className="ot-row">
                      {/* the whole row (tick, number, name) toggles the card */}
                      <label className="ot-pick">
                        <input type="checkbox" aria-label={`Print a card for ${name}`} checked={!excluded.has(name)} onChange={() => toggle(name)} />
                        <span className="ot-idx">{idx}</span>
                        <span className="ot-name">{name}</span>
                      </label>
                      {canWrite && <button className="awt-del" title="Remove" aria-label={`Remove ${name}`} disabled={busy} onClick={() => void removeName(name)}><IconTrash size={13} /></button>}
                    </li>
                  );
                })}
                {!shown.length && <li className="ot-row"><span className="muted" style={{ padding: '4px 0' }}>No matches for “{filter}”.</span></li>}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* print preview - one card per selected employee (index = its position in the full roster) */}
      {q.data && toPrint.length > 0 && (
        <>
          <div className="ot-preview-hd no-print">Print preview - {MONTHS[month]} {year} · {toPrint.length} card(s)</div>
          <div className="ot-print-area">
            {/* the printed card is only the employee, the month and the table: no page margin, so the
                browser has nowhere to print its own header and footer (date, title, address, page no.) */}
            <style>{OT_PRINT_PAGE}</style>
            {toPrint.map((name, i) => {
              const dim = daysInMonth(year, month);
              return (
                <section className={`ot-card${i > 0 && !allCards ? ' ot-card--more' : ''}`} key={name}>
                  <div className="ot-meta">
                    <div className="ot-meta-field ot-meta-grow">
                      <span className="ot-meta-value">{name}</span>
                    </div>
                    <div className="ot-meta-field">
                      <span className="ot-meta-label">Period · Μήνας</span>
                      <span className="ot-meta-value">{MONTHS[month]} {year}</span>
                    </div>
                  </div>

                  <table className="ot-grid">
                    <colgroup>
                      <col style={{ width: '15%' }} /><col style={{ width: '9%' }} /><col style={{ width: '15%' }} />
                      <col style={{ width: '12%' }} /><col style={{ width: '29%' }} /><col style={{ width: '20%' }} />
                    </colgroup>
                    <thead>
                      <tr className="ot-hd2">
                        <th>ΗΜΕΡΑ</th><th>ΗΜΕΡ/ΝΙΑ</th><th>ΥΠΕΡΩΡΙΑ</th><th>ΑΔΕΙΑ</th><th>ΠΕΡΙΓΡΑΦΗ</th><th className="ot-sm">ΧΡΕΩΣΗ ΥΠΕΡΩΡΙΑΣ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: dim }, (_, k) => k + 1).map((day) => {
                        const wd = weekday(year, month, day);
                        const we = wd === 'Saturday' || wd === 'Sunday';
                        return (
                          <tr key={day} className={we ? 'ot-we' : undefined}>
                            <td className="ot-day">{wd}</td>
                            <td className="ot-c">{day}</td>
                            <td /><td /><td /><td />
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="ot-total"><td colSpan={2}>ΣΥΝΟΛΟ · TOTAL</td><td /><td /><td /><td /></tr>
                    </tfoot>
                  </table>

                </section>
              );
            })}
          </div>
          {toPrint.length > 1 && (
            <button type="button" className="btn ot-more no-print" aria-expanded={allCards} onClick={() => setAllCards((v) => !v)}>
              {allCards ? 'Show the first card only' : `Show all ${toPrint.length} cards`}
            </button>
          )}
        </>
      )}

      {imp && (
        <Modal title="Import employees from Excel" onClose={() => setImp(null)}
          footer={<>
            <button className="btn btn-ghost" onClick={() => setImp(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={!imp.fresh.length || importList.isPending}
              onClick={() => { importList.mutate({ names: imp.names }); setImp(null); }}>
              Add {imp.fresh.length} new
            </button>
          </>}>
          <p style={{ marginTop: 0 }}>
            <b>{imp.file}</b> - found <b>{imp.names.length}</b> name(s): <b>{imp.fresh.length}</b> new,
            {' '}{imp.names.length - imp.fresh.length} already in the roster.
          </p>
          <div className="ot-imp-list">
            {imp.names.map((n) => (
              <span key={n} className={`ot-chip ${imp.fresh.includes(n) ? 'is-new' : 'is-dup'}`}>{n}</span>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 12 }}>New names are appended to the roster; duplicates are skipped.</p>
        </Modal>
      )}
      {confirmNode}
    </>
  );
}
